import React, {
  useState,
  useRef,
  useEffect,
  useContext,
  useCallback,
} from "react";
import Lobby from "../../components/Lobby/Lobby";
import Loading from "../../components/Loading/Loading";
import UserVideo from "../../components/UserVideo/UserVideo";
import Notification from "../../components/Notification/Notification";
import { RoomContext } from "../../context/RoomContext";
import { MediaContext } from "../../context/MediaContext";
import { SocketContext } from "../../context/SocketContext.jsx";
import { useBasicUserData } from "../../hooks/UserData";
import { useNextOrStopAction, useInterestAction } from "../../hooks/RoomAction";
import useIsMounted from "../../hooks/IsMounted";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/system";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import ChatBoxWrapper from "../../components/ChatBox/ChatBoxWrapper";
import IconButton from "../../components/IconButton/IconButton";
import { isBrowser, isMobile, isTablet } from "react-device-detect";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import SkipNextRoundedIcon from "@mui/icons-material/SkipNextRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import ChatBubbleRoundedIcon from "@mui/icons-material/ChatBubbleRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";

const getDeviceType = () => {
  if (isMobile) return "Mobile";
  if (isTablet) return "Tablet";
  if (isBrowser) return "Desktop";
  return "unknown";
};

const loadingSpinner = (
  <Box
    sx={{
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
    }}
  >
    <Loading />
  </Box>
);

const Main = styled("main", {
  shouldForwardProp: (prop) =>
    prop !== "roomName" &&
    prop !== "open" &&
    prop !== "drawerWidth" &&
    prop !== "drawerShouldOverlay",
})(({ theme, open, roomName, drawerWidth, drawerShouldOverlay }) => ({
  flexGrow: 1,
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: roomName ? -drawerWidth : 0,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: drawerShouldOverlay ? -drawerWidth : 0,
  }),
}));

const Dashboard = () => {
  const [userData, isLoading, error] = useBasicUserData();
  const [showLobby, setShowLobby] = useState(false);
  const [lobbyKey, setLobbyKey] = useState(0);
  const [interestNotification, setInterestNotification] = useState({});
  const [mutualNotification, setMutualNotification] = useState({});
  const [deviceType, setDeviceType] = useState(getDeviceType());
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [alreadyFriendsNotification, setAlreadyFriendsNotification] = useState(
    {}
  );

  const [isPortrait, setIsPortrait] = useState(false);
  const isMobileLandscape = isMobile && !isPortrait;

  const stopHaloTimeoutRef = useRef(null);


  const audioPlayer = useRef();
  const isMounted = useIsMounted();

  const {
    room,
    roomName,
    interestIndicated,
    mutualInterest,
    updateInterestIndicated,
    updateMutualInterest,
    partnerName,
    updatePartnerName,
    partnerCollegeName,
    updatePartnerCollegeName,
    alreadyFriends,
    updateAlreadyFriends,
  } = useContext(RoomContext);
  const { socket } = useContext(SocketContext);
  const { mediaError } = useContext(MediaContext);

  const [next] = useNextOrStopAction("next");
  const [stop] = useNextOrStopAction("stop");
  const [indicateInterest] = useInterestAction();

  const [open, setOpen] = useState(false);

  const textColor = "#D3D3D3";
  const theme = useTheme();
  const isVerySmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.between("md", "lg"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("lg", "xl"));
  const drawerWidth =
    isSmallScreen || isVerySmallScreen ? 300 : isMediumScreen ? 320 : 350;

  const videoGridRef = useRef(null);

  const [drawerStartPosition, setDrawerStartPosition] = useState("10%");
  const [showGlowEffect, setShowGlowEffect] = useState(false);
  const [showStars, setShowStars] = useState(false);
  const [showStopHalo, setShowStopHalo] = useState(false);

  useEffect(() => {
    setDeviceType(getDeviceType());
  }, [setDeviceType]);

  useEffect(() => {
    if (alreadyFriends) {
      setAlreadyFriendsNotification({
        message: "Say hi to a friend!",
        type: "info",
      });
    } else {
      setAlreadyFriendsNotification({});
    }
  }, [alreadyFriends]);

  const updateDrawerStartPosition = () => {
    if (videoGridRef.current) {
      const rect = videoGridRef.current.getBoundingClientRect();
      const startPosition = rect.top;
      setDrawerStartPosition(`${startPosition}px`);
    }
  };

  useEffect(() => {
    if (open) {
      updateDrawerStartPosition();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (roomName && !isMounted()) {
        next(roomName);
      }
    };
  }, [roomName, next, isMounted]);

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleNewMessageNotif = () => {
    if (!open) {
      setHasUnreadMessages(true);
      if (audioPlayer.current) {
        audioPlayer.current.play();
      }
    }
  };

  const NotificationIconWrapper = styled("div")({
    position: "relative",
    display: "flex",
    justifyContent: "center",
  });

  const RedDot = styled("div")({
    width: "10px",
    height: "10px",
    borderRadius: "50%",
    backgroundColor: "red",
    position: "absolute",
    top: "0",
    right: "0",
    transform: "translate(50%, -50%)",
    boxShadow: "0 0 6px rgba(255,0,0,0.8)",
  });

  const checkOrientation = useCallback(() => {
    const isPortraitMode = window.innerHeight > window.innerWidth;
    setIsPortrait(isMobile && isPortraitMode);
  }, []);

  useEffect(() => {
    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    return () => window.removeEventListener("resize", checkOrientation);
  }, [checkOrientation]);

  useEffect(() => {
    if (open) {
      setHasUnreadMessages(false);
    }
  }, [open]);

  useEffect(() => {
    if (hasUnreadMessages && audioPlayer.current) {
      audioPlayer.current.play();
    }
  }, [hasUnreadMessages]);

  const handleStart = useCallback(async () => {
    // Simple analytics hook
    console.log("[analytics] match_start_clicked");
    setShowLobby(true);
  }, []);

  const handleNext = useCallback(async () => {
    console.log("[analytics] next_clicked");

    setShowLobby((prevState) => {
      if (!prevState) {
        return true;
      }
      setLobbyKey((prevKey) => prevKey + 1);
      return true;
    });

    if (roomName) {
      next(roomName);
      updatePartnerName(null);
      updatePartnerCollegeName(null);
      updateAlreadyFriends(false);
    }
  }, [roomName, next, updatePartnerName, updatePartnerCollegeName, updateAlreadyFriends]);

  const handleStop = useCallback(async () => {
    console.log("[analytics] stop_clicked");

    setShowLobby(false);
    stop(roomName);
    updatePartnerName(null);
    updatePartnerCollegeName(null);
    updateAlreadyFriends(false);
  }, [
    roomName,
    stop,
    updatePartnerName,
    updatePartnerCollegeName,
    updateAlreadyFriends,
  ]);

  const handleInterested = useCallback(async () => {
    if (room && !interestIndicated) {
      console.log("[analytics] interest_clicked");
      indicateInterest(roomName);
      updateInterestIndicated(true);
    }
  }, [
    roomName,
    room,
    indicateInterest,
    updateInterestIndicated,
    interestIndicated,
  ]);

  useEffect(() => {
    const shouldGlow = (showLobby || !!room) && !mediaError;

    if (shouldGlow) {
      if (stopHaloTimeoutRef.current) {
        clearTimeout(stopHaloTimeoutRef.current);
      }
      setShowStopHalo(true);

      stopHaloTimeoutRef.current = setTimeout(() => {
        setShowStopHalo(false);
      }, 5000);
    } else {
      setShowStopHalo(false);
    }
  }, [showLobby, room, mediaError]);

  useEffect(() => {
    setInterestNotification({});
    setMutualNotification({});
  }, [room]);

  const handleNextOrStopFromSocket = useCallback(() => {
    setLobbyKey((prevKey) => prevKey + 1);
    setInterestNotification({});
    setMutualNotification({});
    updatePartnerName(null);
    updatePartnerCollegeName(null);
    updateAlreadyFriends(false);
  }, [updatePartnerName, updateAlreadyFriends]);

  const handleMutualInterest = useCallback(() => {
    if (!mutualInterest && interestIndicated) {
      setMutualNotification({
        message: "Mutual interest confirmed!",
        type: "success",
      });
      updateMutualInterest(true);
      console.log("[analytics] mutual_interest");
    }
  }, [updateMutualInterest, mutualInterest, interestIndicated]);

  const handleNextOrStopFromSocketRef = useRef(handleNextOrStopFromSocket);

  useEffect(() => {
    handleNextOrStopFromSocketRef.current = handleNextOrStopFromSocket;
  }, [handleNextOrStopFromSocket]);

  useEffect(() => {
    if (!socket.connected) {
      socket.connect();
    }

    function handleNextSocket() {
      handleNextOrStopFromSocketRef.current();
    }

    function handleStopSocket() {
      handleNextOrStopFromSocketRef.current();
    }

    function handleInterestIndicated() {
      setInterestNotification({
        message: "Your chat partner liked you!",
        type: "info",
      });
    }

    socket.on("next", handleNextSocket);
    socket.on("stop", handleStopSocket);
    socket.on("mutual_interest", handleMutualInterest);
    socket.on("interest_indicated", handleInterestIndicated);

    return () => {
      socket.off("next", handleNextSocket);
      socket.off("stop", handleStopSocket);
      socket.off("mutual_interest", handleMutualInterest);
      socket.off("interest_indicated", handleInterestIndicated);
    };
  }, [socket, handleMutualInterest]);

  const handleChatClick = () => {
    setOpen((prev) => !prev);
  };

  const primaryActionDisabled = Boolean(showLobby || room || mediaError);
  return (
    <Box
      sx={{
        display: "flex",
        overflow: "hidden",
        // use dynamic viewport height so iOS bars don't break layout
        height: "100dvh",
      }}
    >
      <Main
        open={open}
        roomName={roomName}
        drawerWidth={drawerWidth}
        drawerShouldOverlay={isVerySmallScreen}
      >
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            p: 1,
            flexGrow: 1,
          }}
        >
          {!error && isLoading && loadingSpinner}
          {!error && !isLoading && (
            <>
              {/* Greeting */}
              {(!isMobileLandscape) && <Box
                sx={{
                  mb: isMobileLandscape ? 1 : 2,          // tighter on mobile landscape
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    textAlign: "center",
                    textDecoration: "none",
                    color: textColor,
                    zIndex: 1,
                    px: "7px",
                    pb: "5px", // slightly less vertical padding
                    borderRadius: "10px",
                    backgroundColor: !isMobile ? "#36454F": null,
                    fontFamily: "'Chalkboard SE', 'Comic Sans MS', cursive",
                  }}
                >
                  <span>
                    {!roomName && `Hi ${userData.firstName}`}
                    {roomName && partnerName && (
                      <>
                        <span>
                          {partnerName && `${partnerName}<->`}{partnerCollegeName}
                        </span>
                      </>
                    )}
                  </span>
                </Typography>
              </Box>}

              {/* Video / Lobby stage */}
              <Box
              ref={videoGridRef}
              sx={{
                flex: "1 0 auto",
                width: isMobileLandscape ? "80" : "96%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: { xs: 2, sm: 3, md: 4 },
                py: { xs: 1, sm: 1.5 },
              }}
            >
                <Box
                  sx={{
                    width: "100%",
                    // narrower on phones, especially in landscape
                    maxWidth: isMobile
                      ? (isPortrait ? 420 : 580) // mobile portrait vs mobile landscape
                      : 900,                     // tablet / desktop

                    // main fix: keep video short in mobile landscape
                    maxHeight: isMobileLandscape
                      ? "30vh"        // small strip so controls always fit
                      : isPortrait
                      ? "48vh"        // mobile portrait
                      : "70vh",       // larger screens

                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {showLobby ? (
                    <Lobby key={lobbyKey} deviceType={deviceType} />
                  ) : (
                    <UserVideo />
                  )}
                </Box>
              </Box>

              {/* Controls */}
              <Box
                sx={{
                  // no percentage heights; just tuck under the video
                  display: "flex",
                  justifyContent: "center",
                  mt: isMobileLandscape ? 1 : 0.5,
                  pb: isMobileLandscape ? 1 : { xs: 2, sm: 2.5 }, // less bottom padding
                  zIndex: 1,
                  width: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: isMobileLandscape ? 1.5 : 2.5,
                    px: isMobileLandscape ? 2 : 3,
                    py: isMobileLandscape ? 0.8 : 1.5,
                    borderRadius: "999px",
                    backdropFilter: "blur(10px)",
                    "@keyframes pulseGlow": {
                      "0%": { transform: "scale(1)", opacity: 0.45 },
                      "50%": { transform: "scale(1.03)", opacity: 0.85 },
                      "100%": { transform: "scale(1)", opacity: 0.45 },
                    },
                  }}
                >
                  {/* Match (primary) */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {!primaryActionDisabled && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: 64,
                            height: 64,
                            transform: "translate(-50%, -50%)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                            zIndex: 0,
                            background:
                              "radial-gradient(circle, rgba(129,140,248,0.18) 0%, transparent 70%)",
                            boxShadow: "0 0 0 0 rgba(96,165,250,0.7)",
                            animation:
                              "matchHaloPulse 4.0s ease-out infinite",
                            "@keyframes matchHaloPulse": {
                              "0%": {
                                boxShadow:
                                  "0 0 0 0 rgba(96,165,250,0.7)",
                                opacity: 0.9,
                              },
                              "60%": {
                                boxShadow:
                                  "0 0 0 10px rgba(96,165,250,0)",
                                opacity: 0.55,
                              },
                              "100%": {
                                boxShadow:
                                  "0 0 0 0 rgba(96,165,250,0)",
                                opacity: 0.0,
                              },
                            },
                          }}
                        />
                      )}

                      <IconButton
                        onClick={handleStart}
                        disabled={primaryActionDisabled}
                        tooltip="Start matching with students"
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          borderRadius: "999px",
                          boxShadow: primaryActionDisabled
                            ? "none"
                            : "0 0 8px rgba(96,165,250,0.45)",
                          transform: primaryActionDisabled
                            ? "scale(1)"
                            : "scale(1.02)",
                          transition:
                            "transform 0.18s cubic-bezier(0.175,0.885,0.32,1.275), box-shadow 0.18s ease-out, background-color 0.18s ease-out",
                          "&:hover": {
                            transform: primaryActionDisabled
                              ? "scale(1)"
                              : "scale(1.08)",
                            boxShadow: primaryActionDisabled
                              ? "none"
                              : "0 0 14px rgba(147,197,253,0.85)",
                          },
                          "&:active": {
                            transform: primaryActionDisabled
                              ? "scale(1)"
                              : "scale(0.96)",
                          },
                        }}
                      >
                        <PlayCircleFilledWhiteIcon   sx={{ fontSize: isMobileLandscape ? 24 : 30 }}/>
                      </IconButton>
                    </Box>
                  </Box>

                  {/* Next */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={handleNext}
                      disabled={Boolean(!room)}
                      tooltip="Skip to another match"
                      sx={{
                        borderRadius: "999px",
                        "&:hover": {
                          backgroundColor: "rgba(37, 99, 235, 0.8)",
                        },
                      }}
                    >
                      <SkipNextRoundedIcon sx={{ fontSize: isMobileLandscape ? 22 : 26 }} />
                    </IconButton>
                  </Box>

                  {/* Stop */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {showStopHalo && showLobby && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: 64,
                            height: 64,
                            transform: "translate(-50%, -50%)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                            zIndex: 0,
                            animation:
                              "stopHaloPulse 4.0s ease-out infinite",
                            "@keyframes stopHaloPulse": {
                              "0%": {
                                boxShadow:
                                  "0 0 0 0 rgba(248,113,113,0.7)",
                                opacity: 0.9,
                              },
                              "60%": {
                                boxShadow:
                                  "0 0 0 10px rgba(248,113,113,0)",
                                opacity: 0.55,
                              },
                              "100%": {
                                boxShadow:
                                  "0 0 0 0 rgba(248,113,113,0)",
                                opacity: 0.0,
                              },
                            },
                          }}
                        />
                      )}

                      <IconButton
                        onClick={handleStop}
                        disabled={Boolean(!showLobby)}
                        tooltip="End matching for now"
                        sx={{
                          position: "relative",
                          zIndex: 1,
                          borderRadius: "999px",
                          color: "#FF0000",
                          boxShadow: "none",
                          transition:
                            "transform 0.18s cubic-bezier(0.175,0.885,0.32,1.275), background-color 0.18s ease-out, border-color 0.18s ease-out",
                          "&:active": {
                            transform: "scale(0.96)",
                          },
                        }}
                      >
                        <StopCircleRoundedIcon sx={{ fontSize: isMobileLandscape ? 22 : 26 }} />
                      </IconButton>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={handleChatClick}
                      disabled={Boolean(!room || mediaError)}
                      tooltip="Chat with your current match"
                      sx={{
                        borderRadius: "999px",
                        "&:hover": {
                          backgroundColor: "rgba(30, 64, 175, 0.7)",
                        },
                      }}
                    >
                      <NotificationIconWrapper>
                        <ChatBubbleRoundedIcon sx={{ fontSize: isMobileLandscape ? 20 : 24 }} />
                        {!open && hasUnreadMessages && <RedDot />}
                      </NotificationIconWrapper>
                    </IconButton>
                  </Box>

                  {/* Like */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <IconButton
                      onClick={handleInterested}
                      disabled={Boolean(
                        !room ||
                          interestIndicated ||
                          mediaError ||
                          alreadyFriends
                      )}
                      tooltip="Like them!"
                      sx={{
                        borderRadius: "999px",
                        backgroundColor: "rgba(15, 23, 42, 0.9)",
                        "&:hover": {
                          backgroundColor: "rgba(34, 197, 94, 0.7)",
                        },
                      }}
                    >
                      <FavoriteRoundedIcon sx={{ fontSize: isMobileLandscape ? 20 : 24 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Box>

              {/* Trust / notifications etc stay the same */}
              {!isMobileLandscape && (
                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ textAlign: "center", display: "block", opacity: 0.7, color: "white" }}
                  >
                    VibeU is community-moderated. Please be respectful and report
                    any issues from your profile.
                  </Typography>
                </Box>
              )}
              {interestNotification.message && (
                <Notification
                  message={interestNotification.message}
                  type={interestNotification.type}
                />
              )}
              {mutualNotification.message && (
                <Notification
                  message={mutualNotification.message}
                  type={mutualNotification.type}
                />
              )}
              {alreadyFriendsNotification.message && (
                <Notification
                  message={alreadyFriendsNotification.message}
                  type={alreadyFriendsNotification.type}
                />
              )}
            </>
          )}

          {/* error + mediaError blocks unchanged */}
          {error && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <Typography variant="body1" color="white">
                An error occurred
              </Typography>
            </Box>
          )}
          {mediaError && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <Typography variant="body1" color="white">
                An error occurred while getting your media.
              </Typography>
            </Box>
          )}
        </Container>
      </Main>

      {roomName && (
        <ChatBoxWrapper
          open={open}
          handleDrawerClose={handleDrawerClose}
          drawerWidth={drawerWidth}
          roomName={roomName}
          authorId={userData.id}
          onNewMessage={handleNewMessageNotif}
          drawerShouldBeginAt={drawerStartPosition}
        />
      )}
    </Box>
  );
};

export default Dashboard;
